import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressLoyaltyCouponLoyaltyCouponsUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/loyalty-coupons',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/loyalty-coupons',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/loyalty-coupons',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/loyalty-coupons',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/loyalty-coupons',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/loyalty-coupons',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/loyalty-coupons',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/loyalty-coupons',
        destPath: '',
      },
    ],
    '*.pub.wix-code.com': [
      {
        srcPath: '/_api/loyalty-coupons',
        destPath: '',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/loyalty-coupons',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/loyalty-coupons',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_loyalty_coupons';

/**
 * Redeems a customer's loyalty points for a loyalty reward and creates a loyalty coupon.
 *
 * Creating a loyalty coupon also creates a corresponding "reference" coupon with the [Coupons API](https://dev.wix.com/docs/rest/business-management/marketing/coupons/coupons/coupon-object).
 * The customer receives the reference coupon, which they can apply to their order. The loyalty coupon and its corresponding reference coupon
 * are linked and the loyalty coupon's `status` reflects the current state of the reference coupon.
 *
 * Check which loyalty rewards a site has available with List Rewards in the Loyalty Rewards API.
 */
export function redeemPointsForCoupon(
  payload: object
): RequestOptionsFactory<any> {
  function __redeemPointsForCoupon({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.coupon',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.loyalty.coupon.LoyaltyCoupons.RedeemPointsForCoupon',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyCouponLoyaltyCouponsUrl({
        protoPath: '/v1/coupons',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'coupon.createdDate' },
              { path: 'coupon.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'coupon.couponReference.specification.moneyOffAmount' },
              { path: 'coupon.couponReference.specification.percentOffRate' },
              { path: 'coupon.couponReference.specification.fixedPriceAmount' },
              { path: 'coupon.couponReference.specification.minimumSubtotal' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __redeemPointsForCoupon;
}

/**
 * Redeems a current customer's loyalty points for a loyalty reward and creates a loyalty coupon.
 *
 * Creating a loyalty coupon also creates a corresponding "reference" coupon with the [Coupons API](https://dev.wix.com/docs/rest/business-management/marketing/coupons/coupons/coupon-object).
 * The customer receives the reference coupon, which they can apply to their order. The loyalty coupon and its corresponding reference coupon
 * are linked and the loyalty coupon's `status` reflects the current state of the reference coupon.
 *
 * Check which loyalty rewards a site has available with List Rewards in the Loyalty Rewards API.
 *
 * >**Note:**
 * >This endpoint requires [visitor or member authentication](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities).
 */
export function redeemCurrentMemberPointsForCoupon(
  payload: object
): RequestOptionsFactory<any> {
  function __redeemCurrentMemberPointsForCoupon({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.coupon',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.loyalty.coupon.LoyaltyCoupons.RedeemCurrentMemberPointsForCoupon',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyCouponLoyaltyCouponsUrl({
        protoPath: '/v1/coupons/redeem-my-coupon',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'coupon.createdDate' },
              { path: 'coupon.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'coupon.couponReference.specification.moneyOffAmount' },
              { path: 'coupon.couponReference.specification.percentOffRate' },
              { path: 'coupon.couponReference.specification.fixedPriceAmount' },
              { path: 'coupon.couponReference.specification.minimumSubtotal' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __redeemCurrentMemberPointsForCoupon;
}

/** Retrieves a loyalty coupon. */
export function getLoyaltyCoupon(payload: object): RequestOptionsFactory<any> {
  function __getLoyaltyCoupon({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.coupon',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.loyalty.coupon.LoyaltyCoupons.GetLoyaltyCoupon',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyCouponLoyaltyCouponsUrl({
        protoPath: '/v1/coupons/{loyaltyCouponId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'loyaltyCoupon.createdDate' },
              { path: 'loyaltyCoupon.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'loyaltyCoupon.couponReference.specification.moneyOffAmount',
              },
              {
                path: 'loyaltyCoupon.couponReference.specification.percentOffRate',
              },
              {
                path: 'loyaltyCoupon.couponReference.specification.fixedPriceAmount',
              },
              {
                path: 'loyaltyCoupon.couponReference.specification.minimumSubtotal',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getLoyaltyCoupon;
}

/** Retrieves a sequence of loyalty coupons. */
export function bulkGetLoyaltyCoupon(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkGetLoyaltyCoupon({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.coupon',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.loyalty.coupon.LoyaltyCoupons.BulkGetLoyaltyCoupon',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyCouponLoyaltyCouponsUrl({
        protoPath: '/v1/bulk/coupons',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'couponsInSite.loyaltyCoupons.createdDate' },
              { path: 'couponsInSite.loyaltyCoupons.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'couponsInSite.loyaltyCoupons.couponReference.specification.moneyOffAmount',
              },
              {
                path: 'couponsInSite.loyaltyCoupons.couponReference.specification.percentOffRate',
              },
              {
                path: 'couponsInSite.loyaltyCoupons.couponReference.specification.fixedPriceAmount',
              },
              {
                path: 'couponsInSite.loyaltyCoupons.couponReference.specification.minimumSubtotal',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkGetLoyaltyCoupon;
}

/**
 * Retrieves the loyalty coupons for the currently logged-in member.
 *
 * >**Note:**
 * >This method requires [visitor or member authentication](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).
 */
export function getCurrentMemberCoupons(
  payload: object
): RequestOptionsFactory<any> {
  function __getCurrentMemberCoupons({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.coupon',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.loyalty.coupon.LoyaltyCoupons.GetCurrentMemberCoupons',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyCouponLoyaltyCouponsUrl({
        protoPath: '/v1/coupons/my-coupons',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'loyaltyCoupons.createdDate' },
              { path: 'loyaltyCoupons.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'loyaltyCoupons.couponReference.specification.moneyOffAmount',
              },
              {
                path: 'loyaltyCoupons.couponReference.specification.percentOffRate',
              },
              {
                path: 'loyaltyCoupons.couponReference.specification.fixedPriceAmount',
              },
              {
                path: 'loyaltyCoupons.couponReference.specification.minimumSubtotal',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getCurrentMemberCoupons;
}

/** Retrieves a list of loyalty coupons, with the specified paging, filtering, and sorting. */
export function queryLoyaltyCoupon(
  payload: object
): RequestOptionsFactory<any> {
  function __queryLoyaltyCoupon({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.coupon',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.loyalty.coupon.LoyaltyCoupons.QueryLoyaltyCoupon',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyCouponLoyaltyCouponsUrl({
        protoPath: '/v1/coupons/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'loyaltyCoupons.createdDate' },
              { path: 'loyaltyCoupons.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'loyaltyCoupons.couponReference.specification.moneyOffAmount',
              },
              {
                path: 'loyaltyCoupons.couponReference.specification.percentOffRate',
              },
              {
                path: 'loyaltyCoupons.couponReference.specification.fixedPriceAmount',
              },
              {
                path: 'loyaltyCoupons.couponReference.specification.minimumSubtotal',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryLoyaltyCoupon;
}

/**
 * Deletes a loyalty coupon.
 *
 * The deletion of a loyalty coupon does not impact the functionality of the corresponding coupon itself.
 */
export function deleteLoyaltyCoupon(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteLoyaltyCoupon({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.coupon',
      method: 'DELETE' as any,
      methodFqn:
        'com.wixpress.loyalty.coupon.LoyaltyCoupons.DeleteLoyaltyCoupon',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyCouponLoyaltyCouponsUrl({
        protoPath: '/v1/coupons/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteLoyaltyCoupon;
}
